/**
 * bridge.js (LADO HOST) — el runtime del puente del host (A3, ADR 0003,
 * refs loomitz/iris#24). Es la ÚNICA vía de Iris hacia el DOM/estado del host.
 *
 * Se sirve versionado por URL desde el dominio de Iris (`/bridge/v1/bridge.js`,
 * emitido por `bridge.entry.ts` en build lib). El sitio host lo carga y le pasa
 * su ConfigHost (window.IrisHostConfig o `IrisBridge.init({...})` con
 * `proveedorCarrito`). Responsabilidades:
 *
 *   1. Validar ConfigHost estrictamente (warn TEMPRANO de typos/campos raros).
 *   2. Crear el iframe del shell (`?host=` + `?pagina=` + `?sitio=`).
 *   3. Handshake por contrato: al recibir `hola_shell`, VERIFICAR
 *      event.origin === origen del shell Y event.source === contentWindow del
 *      iframe; negociar major; responder `hola_puente` con targetOrigin
 *      EXPLÍCITO (jamás '*') transfiriendo un MessagePort.
 *   4. Despachar comandos ENTRANTES (por el puerto) contra la allowlist
 *      efectiva (fail-closed) y responder tipado (`nucleo.despacharComando`).
 *
 * El cierre de comandos/orígenes vive en `nucleo.ts` (lógica pura) y en
 * `ConfigSitio` (lado shell). Aquí sólo el transporte, con seams inyectables
 * para el harness de dos extremos (jsdom/happy-dom sin MessageChannel).
 */
import {
  PROTO,
  PUENTE_V,
  esComando,
  esEvento,
  esHolaShell,
  type CatalogoTours,
  type ComandoNombre,
  type ConfigHost,
  type HolaPuente,
  type TourEventoPayload,
  type WidgetPresentarPayload,
  type WidgetPresentarRespuesta
} from "./tipos"
import {
  allowlistEfectiva,
  capacidadesEfectivas,
  comandosImplementados,
  despacharComando,
  type DespachoCtx
} from "./nucleo"
import { cargarCatalogoTours, crearMotorTours, type MotorTours } from "./tour-engine"

/** Puerto tipo-MessagePort (real en browser, fake en tests). */
export interface PuertoLike {
  postMessage(data: unknown, transfer?: Transferable[]): void
  onmessage: ((ev: { data: unknown }) => void) | null
  start?(): void
  close?(): void
}
/** Canal tipo-MessageChannel. */
export interface CanalLike {
  port1: PuertoLike
  port2: PuertoLike
}
/** Ventana receptora de 'message' (window real o EventTarget fake). */
export interface VentanaLike {
  addEventListener(tipo: "message", handler: (ev: MensajeVentana) => void): void
  removeEventListener(tipo: "message", handler: (ev: MensajeVentana) => void): void
}
/** El subconjunto de MessageEvent que consumimos del handshake. */
export interface MensajeVentana {
  data: unknown
  origin: string
  source: unknown
  ports?: ReadonlyArray<PuertoLike>
}
/** Ventana del iframe a la que respondemos (contentWindow). */
export interface VentanaDestino {
  postMessage(data: unknown, targetOrigin: string, transfer?: Transferable[]): void
}

export interface PuenteHostOpciones {
  /** ConfigHost cruda del sitio host (se valida aquí). */
  configHost: ConfigHost
  /** Origen EXACTO del shell de Iris (para verificar event.origin). */
  shellOrigin: string
  /** pathname del host al momento del handshake (PaginaPath). */
  pagina: string
  /** Callback del host que provee el carrito crudo (defensa: se proyecta). */
  proveedorCarrito?: () => unknown
  refrescarCarrito?: DespachoCtx["refrescarCarrito"]
  cargarSeleccionCotizador?: DespachoCtx["cargarSeleccionCotizador"]
  agregarSeleccionCarrito?: DespachoCtx["agregarSeleccionCarrito"]
  abrirModalDiagrama?: DespachoCtx["abrirModalDiagrama"]
  /** Aplica presets de presentación al iframe; devuelve el estado efectivo. */
  presentar?: (payload: WidgetPresentarPayload) => WidgetPresentarRespuesta
  /** Retira el iframe de la interacción tras persistir una preferencia personal. */
  ocultar?: () => boolean | void
  /** Ventana receptora del handshake (default: globalThis.window). */
  ventana?: VentanaLike
  /** Crea el canal del puerto (default: () => new MessageChannel()). */
  crearCanal?: () => CanalLike
  /** Metadatos informativos del handshake. */
  catalogoVersion?: number
  catalogoDigest?: string
  bridgeVersion?: string
  tours?: string[]
  deprecados?: ComandoNombre[]
  /** Motor de tours YA construido (inyección síncrona; harness de tests). */
  motorTours?: MotorTours
  /**
   * Carga ASÍNCRONA del catálogo local (A4). Si se provee junto con
   * {@link PuenteHostOpciones.crearMotor}, el handshake AGUARDA su resolución
   * (bounded por `timeoutCapacidadesMs`) antes de enviar `hola_puente`: así se
   * anuncian los `tours` reales y se cablea `ctx.motorTours`. Fail-open a
   * sin-tours (catálogo `null`, timeout o error ⇒ `hola_puente.tours = []`).
   */
  cargarCatalogo?: () => Promise<CatalogoTours | null>
  /** Fábrica del motor a partir del catálogo cargado + el emisor de eventos del puerto. */
  crearMotor?: (
    catalogo: CatalogoTours,
    emitirEvento: (payload: TourEventoPayload) => void
  ) => MotorTours
  /** Cota del await de capacidades en el handshake (default 4000 ms). */
  timeoutCapacidadesMs?: number
  /** Se dispara una vez, tras validar `hola_shell` y antes de responder al shell. */
  onHandshake?: () => void
  /** Aviso accionable para el dev (default: console.warn). */
  onAviso?: (mensaje: string) => void
}

export interface PuenteHost {
  /** El iframe/ventana destino ya cableado (null si se inyectó VentanaDestino). */
  readonly hola: HolaPuente
  /**
   * Notifica al shell que el host navegó (mantiene `channel_ctx.page` vivo: el
   * shell corre en un iframe y no puede leer la URL del host). El host lo llama
   * desde su router. No-op antes del handshake o si `pagina` no es un PaginaPath
   * (pathname SOLO, sin query/hash/PII).
   */
  emitirPaginaCambiada(pagina: string): void
  /** Detiene el listener de handshake (idempotente). */
  destruir(): void
}

const CLAVES_CONFIG_HOST = new Set(["sitio", "comandos", "capacidades", "toursUrl", "carrito"])
const RE_SITIO = /^[a-z][a-z0-9-]*$/
const RE_COMANDO = /^[a-z][a-z0-9_]*$/
const RE_CAPACIDAD = /^[a-z][a-z0-9_]*@[0-9]+$/
/** `$defs/PaginaPath` — pathname SOLO (sin query/hash/protocol-relative). */
const RE_PAGINA = /^\/(?!\/)[a-zA-Z0-9/_.:-]*$/
/**
 * Valida un PaginaPath (mismo `$def` que usan `emitirPaginaCambiada` e
 * `initIrisBridge`): un solo helper, una sola regla — el emisor JAMÁS anuncia
 * ni envía un pathname que no cumpla su propio contrato.
 */
function esPaginaPathValida(pagina: unknown): pagina is string {
  return typeof pagina === "string" && pagina.length <= 200 && RE_PAGINA.test(pagina)
}

/**
 * Valida ConfigHost estrictamente y avisa TEMPRANO (en dev, no en prod) de
 * typos de llaves, comandos mal formados y `carrito:true` sin proveedor. Lanza
 * si la config es inutilizable (fail-closed: sin config válida no hay puente).
 */
export function validarConfigHost(
  raw: unknown,
  proveedorCarrito: (() => unknown) | undefined,
  onAviso: (mensaje: string) => void
): ConfigHost {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("[iris-bridge] ConfigHost ausente o no es un objeto")
  }
  const src = raw as Record<string, unknown>

  for (const clave of Object.keys(src)) {
    if (!CLAVES_CONFIG_HOST.has(clave)) {
      onAviso(`ConfigHost: llave desconocida '${clave}' (¿typo?) — se ignora`)
    }
  }

  if (typeof src.sitio !== "string" || !RE_SITIO.test(src.sitio) || src.sitio.length > 32) {
    throw new Error("[iris-bridge] ConfigHost.sitio inválido")
  }
  const comandos: ComandoNombre[] = []
  if (src.comandos !== undefined) {
    if (!Array.isArray(src.comandos)) {
      throw new Error("[iris-bridge] ConfigHost.comandos debe ser un arreglo")
    }
    for (const c of src.comandos) {
      if (typeof c !== "string" || !RE_COMANDO.test(c) || c.length > 64) {
        onAviso(`ConfigHost.comandos: '${String(c)}' mal formado — se ignora`)
        continue
      }
      if (!comandos.includes(c)) comandos.push(c)
    }
  }

  const capacidades: string[] = []
  if (src.capacidades !== undefined) {
    if (!Array.isArray(src.capacidades)) {
      throw new Error("[iris-bridge] ConfigHost.capacidades debe ser un arreglo")
    }
    for (const c of src.capacidades) {
      if (typeof c !== "string" || !RE_CAPACIDAD.test(c) || c.length > 64) {
        onAviso(`ConfigHost.capacidades: '${String(c)}' mal formada — se ignora`)
        continue
      }
      if (!capacidades.includes(c)) capacidades.push(c)
    }
  }

  if (comandos.length === 0 && capacidades.length === 0) {
    throw new Error("[iris-bridge] ConfigHost sin comandos/capacidades válidas")
  }

  const config: ConfigHost = { sitio: src.sitio }
  if (comandos.length > 0) config.comandos = comandos
  if (capacidades.length > 0) config.capacidades = capacidades
  if (typeof src.toursUrl === "string") config.toursUrl = src.toursUrl
  if (typeof src.carrito === "boolean") config.carrito = src.carrito

  if (config.carrito === true && !proveedorCarrito) {
    onAviso(
      "ConfigHost.carrito=true pero no se pasó proveedorCarrito(): carrito_snapshot responderá capacidad_no_configurada"
    )
  }
  return config
}

/** Construye el sobre `hola_puente` con la allowlist efectiva anunciada. */
export function construirHolaPuente(
  config: ConfigHost,
  efectiva: ComandoNombre[],
  opts: PuenteHostOpciones
): HolaPuente {
  const hola: HolaPuente = {
    proto: PROTO,
    v: PUENTE_V,
    tipo: "hola_puente",
    sitio: config.sitio,
    version: PUENTE_V,
    comandos: efectiva,
    capacidades: capacidadesEfectivas(config, efectiva),
    tours: opts.tours ?? [],
    pagina: opts.pagina,
    catalogoVersion: opts.catalogoVersion ?? 0
  }
  if (opts.catalogoDigest) hola.catalogoDigest = opts.catalogoDigest
  if (opts.bridgeVersion) hola.bridgeVersion = opts.bridgeVersion
  if (opts.deprecados && opts.deprecados.length > 0) hola.deprecados = opts.deprecados
  return hola
}

/**
 * Cablea el lado HOST del puente sobre una VentanaDestino ya resuelta (el
 * contentWindow del iframe). Núcleo transport-agnóstico usado tanto por la
 * auto-inicialización (`initIrisBridge`) como por el harness de tests.
 *
 * `esFuente(source)` decide si el `event.source` del handshake es el iframe
 * legítimo (en producción: source === iframe.contentWindow).
 */
export function crearPuenteHost(
  destino: VentanaDestino,
  esFuente: (source: unknown) => boolean,
  opts: PuenteHostOpciones
): PuenteHost {
  const onAviso = opts.onAviso ?? ((m: string) => console.warn(m))
  const ventana = opts.ventana ?? (globalThis as unknown as { window?: VentanaLike }).window
  if (!ventana) throw new Error("[iris-bridge] no hay ventana para el handshake")
  const crearCanal = opts.crearCanal ?? (() => new MessageChannel() as unknown as CanalLike)

  const config = validarConfigHost(opts.configHost, opts.proveedorCarrito, onAviso)
  const efectiva = allowlistEfectiva(config, (m) => onAviso(m))
  let holaActual = construirHolaPuente(config, efectiva, opts)

  const ctx: DespachoCtx = {
    allowlistEfectiva: new Set(efectiva),
    proveedorCarrito: opts.proveedorCarrito,
    refrescarCarrito: opts.refrescarCarrito,
    presentar: opts.presentar,
    ocultar: opts.ocultar,
    motorTours: opts.motorTours,
    cargarSeleccionCotizador: opts.cargarSeleccionCotizador,
    agregarSeleccionCarrito: opts.agregarSeleccionCarrito,
    abrirModalDiagrama: opts.abrirModalDiagrama
  }

  let puerto: PuertoLike | null = null
  let handshakeHecho = false

  const manejarComando = async (data: unknown): Promise<void> => {
    if (esComando(data)) {
      const respuesta = await despacharComando(data, ctx)
      puerto?.postMessage(respuesta)
      return
    }
    // Asimetría US28: un evento (u otro sobre) que llega por el puerto se IGNORA
    // (el shell→puente sólo emite comandos; nada más es accionable aquí).
    if (esEvento(data)) return
    onAviso("[iris-bridge] mensaje no-comando en el puerto: se ignora")
  }

  /** El motor emite `tour_evento` hacia el shell por el puerto ya autenticado. */
  const emitirTourEvento = (payload: TourEventoPayload): void => {
    puerto?.postMessage({
      proto: PROTO,
      v: PUENTE_V,
      tipo: "evento",
      evento: "tour_evento",
      payload
    })
  }

  /**
   * Carga (bounded) el catálogo y cablea el motor de tours ANTES de anunciar
   * `hola_puente`, para que `tours` refleje el catálogo real. Fail-open: catálogo
   * `null`/timeout/error ⇒ sin tours (el seam `capacidad_no_configurada` sigue).
   * Sin `cargarCatalogo`/`crearMotor` (o con `motorTours` ya inyectado) → no-op.
   */
  const prepararCapacidades = async (): Promise<void> => {
    if (ctx.motorTours || !opts.cargarCatalogo || !opts.crearMotor) return
    const catalogo = await conCota(opts.cargarCatalogo(), opts.timeoutCapacidadesMs ?? 4000)
    if (!catalogo) return
    const motor = opts.crearMotor(catalogo, emitirTourEvento)
    ctx.motorTours = motor
    holaActual = construirHolaPuente(config, efectiva, {
      ...opts,
      tours: motor.tours()
    })
  }

  const onMensajeVentana = (ev: MensajeVentana): void => {
    // FAIL-CLOSED: sólo el handshake del shell legítimo pasa.
    if (ev.origin !== opts.shellOrigin) return // origen no allowlisteado → SILENCIO
    if (!esFuente(ev.source)) return // no es el iframe esperado → SILENCIO
    if (handshakeHecho) return // un segundo hola_shell no re-negocia
    if (!esHolaShell(ev.data)) return
    if (!ev.data.versiones.includes(PUENTE_V)) {
      // Sin major común: NO se completa handshake (no hay id para correlacionar
      // un error). El shell agota su timeout y no monta. Aviso para el dev.
      onAviso(
        `[iris-bridge] hola_shell sin major común (soporto v${PUENTE_V}, ofrece [${ev.data.versiones.join(",")}])`
      )
      return
    }

    handshakeHecho = true
    opts.onHandshake?.()
    const canal = crearCanal()
    puerto = canal.port1
    puerto.onmessage = (m) => {
      void manejarComando(m.data)
    }
    puerto.start?.()

    // targetOrigin EXPLÍCITO (jamás '*'); el port2 viaja como Transferable.
    const enviarHola = (): void =>
      destino.postMessage(holaActual, opts.shellOrigin, [canal.port2 as unknown as Transferable])

    // El shell aún no tiene port2, así que ningún comando llega antes de enviar
    // hola_puente: podemos AGUARDAR las capacidades (motor de tours) sin perder
    // mensajes. SIN carga async el envío es SÍNCRONO (path A3 intacto).
    if (!ctx.motorTours && opts.cargarCatalogo && opts.crearMotor) {
      void prepararCapacidades().then(enviarHola)
    } else {
      enviarHola()
    }
  }

  ventana.addEventListener("message", onMensajeVentana)

  const emitirPaginaCambiada = (pagina: string): void => {
    // Una navegación puede ocurrir mientras el iframe todavía negocia. En ese
    // caso actualizamos el hola pendiente; tras el handshake también emitimos
    // por el puerto. Siempre se valida antes para no propagar query/hash/PII.
    if (!esPaginaPathValida(pagina)) return
    holaActual = { ...holaActual, pagina }
    if (!handshakeHecho || !puerto) return
    puerto.postMessage({
      proto: PROTO,
      v: PUENTE_V,
      tipo: "evento",
      evento: "pagina_cambiada",
      payload: { pagina }
    })
  }

  return {
    // Getter: `holaActual` se rehace si las capacidades async cambian `tours`.
    get hola() {
      return holaActual
    },
    emitirPaginaCambiada,
    destruir() {
      ventana.removeEventListener("message", onMensajeVentana)
      ctx.motorTours?.destruir()
      puerto?.close?.()
      puerto = null
    }
  }
}

/** Corre una promesa con cota; resuelve `null` si vence (jamás cuelga el handshake). */
function conCota<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise<T | null>((resolver) => {
    let hecho = false
    const t = setTimeout(() => {
      if (!hecho) {
        hecho = true
        resolver(null)
      }
    }, ms)
    p.then(
      (v) => {
        if (!hecho) {
          hecho = true
          clearTimeout(t)
          resolver(v)
        }
      },
      () => {
        if (!hecho) {
          hecho = true
          clearTimeout(t)
          resolver(null)
        }
      }
    )
  })
}

// --- Presentación: el bridge mapea el preset a CSS del iframe (contrato §3) --

/** Base fija del iframe: pegado a la esquina, por ENCIMA de todo. */
const IFRAME_BASE =
  "position:fixed;inset-block-end:0.75rem;inset-inline-end:0.75rem;box-sizing:border-box;border:0;border-radius:26px;overflow:hidden;background:transparent;z-index:2147483000;"
const IFRAME_OPEN_BORDER = "border:1px solid rgba(120,120,120,0.28);"
export const IRIS_BRIDGE_FRAME_ATTR = "data-iris-bridge-frame"
export const IRIS_BRIDGE_PRESENTATION_ATTR = "data-iris-presentacion"
export const IRIS_BRIDGE_FRAME_STYLE_ID = "iris-bridge-frame-style"
export const IRIS_BRIDGE_MOBILE_FULLSCREEN_CSS = `
@media (max-width: 640px) {
  iframe[${IRIS_BRIDGE_FRAME_ATTR}="true"][${IRIS_BRIDGE_PRESENTATION_ATTR}="abierto"] {
    position: fixed !important;
    inset: 0 !important;
    inset-block-start: 0 !important;
    inset-block-end: 0 !important;
    inset-inline-start: 0 !important;
    inset-inline-end: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important;
    max-width: none !important;
    max-height: none !important;
    border: 0 !important;
    border-radius: 0 !important;
  }
}
`

export function ensureIrisBridgeFrameStyle(doc: Document = document): HTMLStyleElement | null {
  const existing = doc.getElementById(IRIS_BRIDGE_FRAME_STYLE_ID)
  if (existing?.tagName === "STYLE") return existing as HTMLStyleElement
  const style = doc.createElement("style")
  style.id = IRIS_BRIDGE_FRAME_STYLE_ID
  style.textContent = IRIS_BRIDGE_MOBILE_FULLSCREEN_CSS
  ;(doc.head || doc.documentElement).appendChild(style)
  return style
}

/**
 * Geometría del iframe por preset. El estado `cerrado` NO es 0×0: encierra sólo
 * el launcher (caja mínima en la esquina, para no tapar el resto de la página);
 * `abierto` crece para contener el panel. `amplio` da más área que `compacto`.
 */
export function cssIframe(estado: "abierto" | "cerrado", tamano: "compacto" | "amplio"): string {
  if (estado === "cerrado") {
    return IFRAME_BASE + "width:156px;height:156px;"
  }
  const ancho =
    tamano === "amplio"
      ? "min(820px,calc(100vw - 1.5rem))"
      : "min(560px,calc(100vw - 1.5rem))"
  const alto =
    tamano === "amplio"
      ? "min(780px,calc(100vh - 1.5rem))"
      : "min(680px,calc(100vh - 1.5rem))"
  return IFRAME_BASE + IFRAME_OPEN_BORDER + `width:${ancho};height:${alto};`
}

function cssIframeOculto(): string {
  return cssIframe("cerrado", "compacto") + "visibility:hidden;pointer-events:none;"
}

/** El subconjunto del iframe que el presentador muta (real en browser, fake en tests). */
export interface IframeEstilable {
  style: { cssText: string }
  setAttribute?: (name: string, value: string) => void
}

/**
 * Presentador POR DEFECTO del bridge cuando el host no pasa el suyo: aplica de
 * verdad el CSS al iframe (redimensiona) y devuelve el estado EFECTIVO — cumple
 * `WidgetPresentarRespuesta` ("estado EFECTIVO tras aplicar los presets"), sin
 * mentir. Un preset ausente conserva la dimensión vigente.
 */
export function crearPresentadorIframe(
  iframe: IframeEstilable,
  estadoInicial: WidgetPresentarRespuesta = {
    estado: "cerrado",
    tamano: "compacto"
  }
): (payload: WidgetPresentarPayload) => WidgetPresentarRespuesta {
  let actual = estadoInicial
  return (payload) => {
    const efectivo: WidgetPresentarRespuesta = {
      estado: payload.estado ?? actual.estado,
      tamano: payload.tamano ?? actual.tamano
    }
    iframe.style.cssText = cssIframe(efectivo.estado, efectivo.tamano)
    iframe.setAttribute?.(IRIS_BRIDGE_PRESENTATION_ATTR, efectivo.estado)
    actual = efectivo
    return efectivo
  }
}

/** Retira visual e interactivamente el iframe sin geometría controlada por el shell. */
export function crearOcultadorIframe(
  iframe: IframeEstilable
): () => true {
  return () => {
    iframe.style.cssText = cssIframeOculto()
    iframe.setAttribute?.(IRIS_BRIDGE_PRESENTATION_ATTR, "oculto")
    iframe.setAttribute?.("aria-hidden", "true")
    return true
  }
}

// --- Auto-inicialización en el sitio host -----------------------------------

interface IrisHostConfigGlobal extends ConfigHost {
  /** URL completa del shell de Iris (deriva shellOrigin). */
  shellUrl?: string
  bridgeVersion?: string
}

/**
 * Lee la config del host (window.IrisHostConfig o los data-attrs del `<script>`
 * del bridge), crea el iframe del shell y cablea el puente. El `proveedorCarrito`
 * sólo llega por la variante JS `IrisBridge.init({...})`.
 */
export function initIrisBridge(
  overrides: Partial<PuenteHostOpciones> & {
    shellUrl?: string
    configHost?: ConfigHost
    proveedorCarrito?: () => unknown
  } = {}
): PuenteHost | null {
  if (typeof document === "undefined" || typeof window === "undefined") return null
  const onAviso = overrides.onAviso ?? ((m: string) => console.warn(m))

  const global = (window as unknown as { IrisHostConfig?: IrisHostConfigGlobal }).IrisHostConfig
  const configHost = overrides.configHost ?? global
  if (!configHost) {
    onAviso("[iris-bridge] sin ConfigHost (window.IrisHostConfig ausente): no se monta")
    return null
  }

  const shellUrl = overrides.shellUrl ?? global?.shellUrl
  if (!shellUrl) {
    onAviso("[iris-bridge] falta shellUrl del shell de Iris: no se monta")
    return null
  }
  let shellOrigin: string
  try {
    shellOrigin = new URL(shellUrl).origin
  } catch {
    onAviso("[iris-bridge] shellUrl inválida: no se monta")
    return null
  }

  // Misma validación que `emitirPaginaCambiada` (RE_PAGINA/esPaginaPathValida):
  // el emisor jamás viola su propio contrato PaginaPath, ni deja viajar el
  // pathname crudo del host (query/hash/PII/protocol-relative/>200 chars) hacia
  // `hola_puente.pagina` NI hacia la query `?pagina=` del iframe. Inválida → '/'.
  const paginaCruda = overrides.pagina ?? (window.location.pathname || "/")
  const pagina = esPaginaPathValida(paginaCruda) ? paginaCruda : "/"

  // Iframe del shell: ?host=<origen del host> & ?pagina=<path> & ?sitio=<sitio>.
  const src = new URL(shellUrl)
  src.searchParams.set("host", window.location.origin)
  src.searchParams.set("pagina", pagina)
  src.searchParams.set("sitio", configHost.sitio)

  ensureIrisBridgeFrameStyle(document)

  const iframe = document.createElement("iframe")
  iframe.setAttribute("title", "Iris")
  iframe.setAttribute("aria-hidden", "true")
  iframe.setAttribute(IRIS_BRIDGE_FRAME_ATTR, "true")
  iframe.setAttribute(IRIS_BRIDGE_PRESENTATION_ATTR, "cerrado")
  // Antes del handshake válido el iframe existe para que el shell cargue, pero
  // no se muestra ni captura clicks. Si ConfigSitio viene apagada, el shell no
  // saluda y el launcher nunca aparece en el host.
  iframe.style.cssText = cssIframeOculto()
  iframe.src = src.toString()
  document.body.appendChild(iframe)

  const mostrarIframe = (): void => {
    iframe.setAttribute("aria-hidden", "false")
    if (overrides.presentar) {
      iframe.style.visibility = ""
      iframe.style.pointerEvents = ""
      return
    }
    iframe.style.cssText = cssIframe("cerrado", "compacto")
  }

  const destino: VentanaDestino = {
    postMessage: (data, targetOrigin, transfer) =>
      iframe.contentWindow?.postMessage(data, targetOrigin, transfer)
  }
  const esFuente = (source: unknown): boolean => source === iframe.contentWindow

  // Motor de tours (A4): sólo si el host publicó un `toursUrl`. El catálogo se
  // carga same-origin y se valida al vuelo (fail-closed); la página VIVA del
  // host (para `pagina_no_aplica`) se lee de `window.location` al ejecutar cada
  // comando — el motor usa `globalThis.document` para consultar las anclas.
  const paginaViva = (): string => {
    const p = window.location.pathname || "/"
    return esPaginaPathValida(p) ? p : "/"
  }
  const tours: Pick<PuenteHostOpciones, "cargarCatalogo" | "crearMotor"> = configHost.toursUrl
    ? {
        cargarCatalogo: () =>
          cargarCatalogoTours(configHost.toursUrl as string, configHost.sitio, { onAviso }),
        crearMotor: (catalogo, emitirEvento) =>
          crearMotorTours({
            catalogo,
            paginaActual: paginaViva,
            emitirEvento
          })
      }
    : {}

  return crearPuenteHost(destino, esFuente, {
    ...overrides,
    ...tours,
    configHost,
    shellOrigin,
    pagina,
    // Sin presentador del host, el bridge aplica su propio CSS al iframe
    // (redimensiona de verdad) al recibir `widget_presentar`.
    presentar: overrides.presentar ?? crearPresentadorIframe(iframe),
    // Ocultamiento personal: quita también pointer-events para no dejar una
    // caja transparente capturando clicks en la esquina del host.
    ocultar: overrides.ocultar ?? crearOcultadorIframe(iframe),
    bridgeVersion: overrides.bridgeVersion ?? global?.bridgeVersion,
    onHandshake: () => {
      mostrarIframe()
      overrides.onHandshake?.()
    },
    onAviso
  })
}

/** API pública global del artefacto servido (`/bridge/v1/bridge.js`). */
export const IrisBridge = {
  init: initIrisBridge,
  /** Comandos implementados por este build (para diagnóstico del host). */
  comandos: (): ComandoNombre[] => comandosImplementados(),
  version: PUENTE_V
}
